import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@pentabd/ui";

import { InputTextRow, MultiFile } from "@components";

import { AprType } from "@type/HRM/apr";

import {
  UPDATE_APR_DOCUMENT,
  aprValidation,
} from "@validations/HRM/employee/aprValidation";
import { convertMultipleDocumentData } from "@utils/documentDataConvert";

export interface AprPropsType {
  createAPR: (newApr: AprType) => void;
  createSuccess?: boolean;
}

export const UpdateAprDocument = ({
  createAPR,
  createSuccess,
}: AprPropsType) => {
  const { t } = useTranslation();
  const methods = useForm<AprType>({
    resolver: yupResolver(aprValidation),
  });

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors },
    control,
    setValue,
  } = methods;

  const onSubmit: SubmitHandler<AprType> = (data: AprType) => {
    createAPR(
      convertMultipleDocumentData(data, [
        UPDATE_APR_DOCUMENT.UPLOAD_APR,
        UPDATE_APR_DOCUMENT.UPLOAD_KPI,
      ]),
    );
  };

  useEffect(() => {
    if (createSuccess) {
      reset();
    }
  }, [createSuccess, methods, reset]);

  return (
    <form
      className="container py-8 d-flex flex-column gap-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Text
        weight="semibold"
        size="lg"
        color="dark"
        className="pb-8 d-block border-bottom"
      >
        {t("EMPLOYEE.APR_INFO.FORM.UPDATE_APR_DOCUMENT_INFO.HEADING")}
      </Text>
      <InputTextRow
        register={{ ...register(UPDATE_APR_DOCUMENT.YEAR) }}
        label="EMPLOYEE.APR_INFO.FORM.UPDATE_APR_DOCUMENT_INFO.YEAR"
        error={errors}
        required
        restriction
      />
      <InputTextRow
        register={{ ...register(UPDATE_APR_DOCUMENT.MARK) }}
        label="EMPLOYEE.APR_INFO.FORM.UPDATE_APR_DOCUMENT_INFO.MARK"
        error={errors}
        required
      />

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t(
              "EMPLOYEE.APR_INFO.FORM.UPDATE_APR_DOCUMENT_INFO.UPLOAD_APR.LABEL",
            )}
            <span className="text-danger">&#42;</span>
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <MultiFile
            id={UPDATE_APR_DOCUMENT.UPLOAD_APR}
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t(
              "EMPLOYEE.APR_INFO.FORM.UPDATE_APR_DOCUMENT_INFO.UPLOAD_KPI.LABEL",
            )}
            <span className="text-danger">&#42;</span>
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <MultiFile
            id={UPDATE_APR_DOCUMENT.UPLOAD_KPI}
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />
        </div>
      </div>

      <div className="row mb-10">
        <div className="text-end col-12 col-lg-10">
          <Button size="sm" htmlType="submit" type="info" fill="outline">
            {t("EMPLOYEE.APR_INFO.FORM.SAVE_BUTTON")}
          </Button>
        </div>
      </div>
    </form>
  );
};
